import { Directive, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudDataSource, CrudListComponent, FormStorage, UserStorage } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, SYSTEM_MESSAGE_TYPE, SystemMessage } from 'assets-model';
import { BusinessUnitService, SystemMessageDataSource, SystemMessageService } from 'assets-service';


@Directive()
export abstract class SystemMessageSearchComponentBase extends CrudListComponent<SystemMessage, number> {
  protected systemConfig: any;

  displayedColumns: string[] = [ 'id', 'businessUnit.buId', 'expirationDate', 'expiryDate', 'linkData', 'message', 'uniqueId', 'userMessageType', 'actions'];

  public searchForm: FormGroup;

  idFilter: number;
  @ViewChild('idFilterInput') idFilterInput: ElementRef;

  businessUnitFilter: BusinessUnit;
  businessUnitSelectionList: BusinessUnit[];
  @ViewChild('businessUnitFilterInput') businessUnitFilterInput: MatSelect;

  expiryDateFilter: Date;
  @ViewChild('expiryDateFilterInput') expiryDateFilterInput: ElementRef;
  expiryDateLessThanFilter: Date;
  @ViewChild('expiryDateLessThanFilterInput') expiryDateLessThanFilterInput: ElementRef;
  expiryDateLessThanOrEqualToFilter: Date;
  @ViewChild('expiryDateLessThanOrEqualToFilterInput') expiryDateLessThanOrEqualToFilterInput: ElementRef;
  expiryDateGreaterThanFilter: Date;
  @ViewChild('expiryDateGreaterThanFilterInput') expiryDateGreaterThanFilterInput: ElementRef;
  expiryDateGreaterThanOrEqualToFilter: Date;
  @ViewChild('expiryDateGreaterThanOrEqualToFilterInput') expiryDateGreaterThanOrEqualToFilterInput: ElementRef;

  messageFilter: string;
  @ViewChild('messageFilterInput') messageFilterInput: ElementRef;


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected systemMessageService: SystemMessageService,
    protected businessUnitService: BusinessUnitService,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(SYSTEM_MESSAGE_TYPE, route, router, userStorage, dialog, systemMessageService, injector);

    this.searchForm = this.fb.group({
      id: [null],
      businessUnit: [null],
      expiryDateLessThan: [null],
      expiryDateLessThanOrEqualTo: [null],
      expiryDateGreaterThan: [null],
      expiryDateGreaterThanOrEqualTo: [null],
      expiryDate: [null],
      message: [null]
    });
  }

  createDataSource(): CrudDataSource<SystemMessage> {
    return new SystemMessageDataSource(this.systemMessageService);
  }

  createServiceArguments(): any {
    return {
      id: this.idFilter,
      buId: this.businessUnitFilter && this.businessUnitFilter.buId,
      expiryDateLessThan: this.expiryDateLessThanFilter,
      expiryDateLessThanOrEqualTo: this.expiryDateLessThanOrEqualToFilter,
      expiryDateGreaterThan: this.expiryDateGreaterThanFilter,
      expiryDateGreaterThanOrEqualTo: this.expiryDateGreaterThanOrEqualToFilter,
      expiryDate: this.expiryDateFilter,
      message: this.messageFilter
    };
  }
  
  clearFiltersBeforeLoadPage() {
    this.restoreFormFromParams(this.searchForm);
  }
  
  clearFilterVariables() {
    this.idFilter = null;
    this.businessUnitFilter = null;
    this.expiryDateLessThanFilter = null;
    this.expiryDateLessThanOrEqualToFilter = null;
    this.expiryDateGreaterThanFilter = null;
    this.expiryDateGreaterThanOrEqualToFilter = null;
    this.expiryDateFilter = null;
    this.messageFilter = null;
  }
  
  clearFilters() {
    this.clearFilterVariables();
    this.clearFiltersBeforeLoadPage();
    this.loadPage();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.addSearchOnChangedInputEventListener(this.idFilterInput);
    this.addSearchOnChangedSelectEventListener(this.businessUnitFilterInput);
    this.addSearchOnChangedInputEventListener(this.expiryDateLessThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.expiryDateLessThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.expiryDateGreaterThanFilterInput);
    this.addSearchOnChangedInputEventListener(this.expiryDateGreaterThanOrEqualToFilterInput);
    this.addSearchOnChangedInputEventListener(this.expiryDateFilterInput);
    this.addSearchOnChangedInputEventListener(this.messageFilterInput);
    this.formStorage.rememberFormChanges(this.searchForm);
  }

  ngOnInit() {
    super.ngOnInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }

  protected initExportOptions(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initExportOptions(config);
  }
  
  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    return {
      id: (value: number) => self.idFilter = value,
      businessUnit: (value: BusinessUnit) => { self.businessUnitFilter = value; this.businessUnitChanged(value, false); },
      expiryDateLessThan: (value: Date) => self.expiryDateLessThanFilter = value,
      expiryDateLessThanOrEqualTo: (value: Date) => self.expiryDateLessThanOrEqualToFilter = value,
      expiryDateGreaterThan: (value: Date) => self.expiryDateGreaterThanFilter = value,
      expiryDateGreaterThanOrEqualTo: (value: Date) => self.expiryDateGreaterThanOrEqualToFilter = value,
      expiryDate: (value: Date) => self.expiryDateFilter = value,
      message: (value: string) => self.messageFilter = value
    }
  }
  
  restoreFormChanges() {
    const self = this;

    // IMPORTANT that the restoreFormChanges call occurs before the initial search - typically best done in onInitAsyncComplete
    // Whereever a form control binds with [ngModel] to a reactive form, ngModel takes precedence,
    // and needs it's own setter function to set the scope attribute bound
    this.formStorage.restoreFormChanges(this.searchForm, this.transformAllPropertiesToModelSetters());
  }

  idChanging(value: number, unsetDependents: boolean = true) {}
  
  idChanged(value: number, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = data.items;
      }
    );
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  

  expiryDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  expiryDateChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  expiryDateLessThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  expiryDateLessThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  expiryDateLessThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  expiryDateLessThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  expiryDateGreaterThanChanging(value: Date, unsetDependents: boolean = true) {}
  
  expiryDateGreaterThanChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  expiryDateGreaterThanOrEqualToChanging(value: Date, unsetDependents: boolean = true) {}
  
  expiryDateGreaterThanOrEqualToChanged(value: Date, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  messageChanging(value: string, unsetDependents: boolean = true) {}
  
  messageChanged(value: string, unsetDependents: boolean = true) {
    /*
    if (unsetDependents) {
      this.depdendentField.setValue(null);
    }
    dependentFieldLoadSelectionList();
    */
  }

  disabledTooltip(element) {
    if (!element) {
      return true;
    }
    return element.valid || !element.touched;
  }

  get id() {
    return this.searchForm && this.searchForm.get('id');
  }

  get businessUnit() {
    return this.searchForm && this.searchForm.get('businessUnit');
  }

  get expiryDate() {
    return this.searchForm && this.searchForm.get('expiryDate');
  }

  get expiryDateLessThan() {
    return this.searchForm && this.searchForm.get('expiryDateLessThan');
  }
  
  get expiryDateLessThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('expiryDateLessThanOrEqualTo');
  }
  
  get expiryDateGreaterThan() {
    return this.searchForm && this.searchForm.get('expiryDateGreaterThan');
  }
  
  get expiryDateGreaterThanOrEqualTo() {
    return this.searchForm && this.searchForm.get('expiryDateGreaterThanOrEqualTo');
  }

  get message() {
    return this.searchForm && this.searchForm.get('message');
  }


}



